/**
 * @format
 * @mixin Mixin to of configUtils Primary shares common config form methods and a ConfigLookups data object
 */
export default {
	data() {
		const isClientConfigUi = this.$cs_.get(this.componentSettings, 'isClientConfigUi') === true;
		const configurableUserConfigUiSections = Array.isArray(this.$cs_.get(this.componentSettings, 'configurableUserConfigUiSections')) ? this.componentSettings.configurableUserConfigUiSections : [];
		const configIsReadOnlyModeForClient = (isClientConfigUi === true) ? this.$root.$data.clientConfigUIReadOnly : false;

		return {
			configLoaded: false,
			isClientConfigUi,
			configurableUserConfigUiSections,
			configIsReadOnlyModeForClient,
			configItemNetworkCallGuid: null,
			fullConfigNetworkCallGuid: null
		};
	},

	created() {
		this.miConfigUtilsCollectionsRegex = /(history|junction)/i;
	},

	methods: {
		/**
		 * @public
		 * @description
		 * Populates the rowData template for a given root config object
		 *
		 * @param {Array} rowData top level array
		 * @param {Array|Object} children the children to add
		 * @param {String} key the key of the path
		 * 
		 */
		miConfigUtilsPopulateRowDataTemplate(newRowData, children, key) {
			let configForm = this.configForms.find(cf => cf.collectionId === key);

			if (configForm == undefined) return;
			
			// ignore if this is client config UI and the section is not selected
			if (this.isClientConfigUi === true && this.configurableUserConfigUiSections.indexOf(key) === -1) return;

			newRowData.push({
				dataPath: [key],
				title: configForm.configCollectionName || 'Unknown',
				uniqueKey: key
			});

			// add children
			if (Array.isArray(children)) {
				for (let i = 0, child; (child = children[i]) !== undefined; i++) {
					if (this.isClientConfigUi === true && key === 'componentSettingsTemplates' && child.value === 'config') {
						continue; //we don't want to show any config ui component settings to clients
					}

					// filter out hidden collections when this is turned on
					if (!this.showHiddenCollection && key === 'collections' && this.miConfigUtilsCollectionsRegex.test(child.value) === true) {
						continue;
					}

					newRowData.push({
						id: child.value,
						uniqueKey: `${key}-${child.value}`,
						dataPath: [key, child.value],
						title: child.label,
						formId: configForm.id,
						collectionId: key
					});
				}
			} else if (this.$_.isPlainObject(children)) {
				newRowData.push({
					id: children.value,
					uniqueKey: `${key}-${children.value}`,
					dataPath: [key, children.value],
					title: children.label,
					formId: configForm.id,
					collectionId: key
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Uses the legacy method to get the full site config
		 * Currently used for the site builder only
		 * TODO: Refactor in 2.2
		 * 
		 * @returns {Object}
		 */
		async miConfigUtilsGetFullSiteConfig() {
			const dataUrl = `${this.$getApiBaseUrl()}/api/SiteConfiguration/GetFullSiteConfiguration`;
			//cancel any existing requests
			if (this.fullConfigNetworkCallGuid != undefined) this.$csNetworkCall.cancelRequest(this.fullConfigNetworkCallGuid);
			//create new fetch request
			this.fullConfigNetworkCallGuid = this.$csNetworkCall.createFetchGetRequest(dataUrl);
			let response = await this.$csNetworkCall.fetchRequest(this.fullConfigNetworkCallGuid);
			//ignore cancelled requests
			if (response == undefined) return;

			this.fullConfigNetworkCallGuid = undefined;

			return response.data;
		},

		/**
		 * @public
		 * @description
		 * Gets the overall site configuration object for this entity and loads it in to data
		 * @returns {Object}
		 *
		 */
		async miConfigUtilsGetSiteConfig() {
			try {
				if (this.gridOptions && this.gridOptions.api && typeof this.gridOptions.api.showLoadingOverlay === 'function') this.gridOptions.api.showLoadingOverlay();

				const dataUrl = `${this.$getApiBaseUrl()}/api/SiteConfiguration/GetFullConfigurationForConfigUI`;

				//cancel any existing requests
				if (this.getConfigurationGuid != undefined) this.$csNetworkCall.cancelRequest(this.getConfigurationGuid);
				//create new fetch request
				this.getConfigurationGuid = this.$csNetworkCall.createFetchGetRequest(dataUrl);
				let response = await this.$csNetworkCall.fetchRequest(this.getConfigurationGuid);
				//ignore cancelled requests
				if (response == undefined) return;

				this.getConfigurationGuid = undefined;

				// transform data from response
				const newRowData = [];
				for (let key in response.data) {
					let children = response.data[key];
					this.miConfigUtilsPopulateRowDataTemplate(newRowData, children, key);
				}

				this.configLoaded = true;
				
				return newRowData;
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000006'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Loads a specific item from the config API
		 * 
		 * @param {String} endpointId The config item type
		 * @param {String} itemId The config item id
		 * 
		 * @returns {Object}
		 */
		async miConfigUtilsGetConfigItem(endpointId, itemId) {
			if (this.configItemNetworkCallGuid != undefined) this.$csNetworkCall.cancelRequest(this.configItemNetworkCallGuid);
			this.configItemNetworkCallGuid = this.$csNetworkCall.createFetchGetRequest(`${this.$getApiBaseUrl()}/api/SiteConfiguration/GetConfigurationForConfigUIById/${endpointId}/${itemId}`);
			let response = await this.$csNetworkCall.fetchRequest(this.configItemNetworkCallGuid);
			this.configItemNetworkCallGuid = undefined;

			return response;
		},

		/**
		 * @public
		 * @description
		 * Populates a form with the correct values from the row data object when in edit mode
		 *
		 * @param {Object} rowData The row data object containing the items values
		 * @param {Object} configForm The form object to be used
		 *
		 * @returns {Object} configForm
		 */
		miConfigUtilsPopulateEditFormValues(rowData, configForm) {
			try {
				if (configForm.sections && Array.isArray(configForm.sections)) {
					for (let s = 0, section; (section = configForm.sections[s]); s++) {
						if (section.fields && Array.isArray(section.fields)) {
							section.fields = this.miConfigUtilsPopulateFieldArrayValues(rowData, section.fields);
						}
					}
				}
								
				return configForm;
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000117'
				});
			}
		},

		/**
		 * @public
		 * @description
		 * Populates an individual array of field values, recursing if necessary
		 *
		 * @param {Object} rowData The row data object containing the items values
		 * @param {Object} configForm The form object to be used
		 *
		 * @returns {Object} configForm
		 */
		miConfigUtilsPopulateFieldArrayValues(rowData, fields) {
			try {
				for (let f = 0, field; (field = fields[f]); f++) {
					if (field.definition.fieldType === 'cs-subsection') {
						field.definition.subFields = this.miConfigUtilsPopulateFieldArrayValues(rowData, field.definition.subFields);
					}

					let fieldNameArray = field.id.split('.');
					field.value = this.$cs_.get(rowData, ...fieldNameArray);
				}

				return fields;
			} catch (error) {
				this.$recordError({
					type: 'error',
					error: error,
					displayToUser: true,
					errorCode: '000118'
				});
			}
		},
		/**
		 * @public
		 * @description
		 * Convenient shared method to get the right config form endpoint for saving
		 * 
		 * @param {String} collectionId 
		 * @param {Object} payload 
		 * @returns {String}
		 */
		miConfigUtilsGetConfigUiCollectionFormUrl(collectionId, payload) {
			let baseUrl = this.$getApiBaseUrl();
			switch (collectionId) {
				case 'apiExport':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddApiExport' : 'UpdateApiExport');
					break;
				case 'locales':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddLocale' : 'UpdateLocale');
					break;
				case 'platformInstanceSettings':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddPlatformInstanceSettings' : 'UpdatePlatformInstanceSettings');
					break;
				case 'queryTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddQueryTemplate' : 'UpdateQueryTemplate');
					break;
				case 'backgroundServices':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddBackgroundService' : 'UpdateBackgroundService');
					break;
				case 'collections':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddCollection' : 'UpdateCollection');
					break;
				case 'fieldCollectionTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddFieldCollectionTemplate' : 'UpdateFieldCollectionTemplate');
					break;
				case 'filterCollectionTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddFilterCollectionTemplate' : 'UpdateFilterCollectionTemplate');
					break;
				case 'siteForms':
					// TODO: Input mask fields
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddSiteForm' : 'UpdateSiteForm');
					break;
				case 'processDiagrams':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddProcessDiagram' : 'UpdateProcessDiagram');
					break;
				case 'siteStructure':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddEntity' : 'UpdateEntity');
					break;
				case 'screens':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddScreen' : 'UpdateScreen');
					break;
				case 'triggerRulesTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddTriggerRuleTemplate' : 'UpdateTriggerRuleTemplate');
					break;
				case 'triggerActionsTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddTriggerActionTemplate' : 'UpdateTriggerActionTemplate');
					break;
				case 'reportCollection':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddReport' : 'UpdateReport');
					break;
				case 'componentSettingsTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddComponentSettings' : 'UpdateComponentSettings');
					break;
				case 'emailTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddEmailTemplate' : 'UpdateEmailTemplate');
					break;
				case 'emails':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddEmail' : 'UpdateEmail');
					break;
				case 'accordionCollection':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddAccordion' : 'UpdateAccordion');
					break;
				case 'themeTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddTheme' : 'UpdateTheme');
					break;
				case 'groups':
					baseUrl += '/api/Identity/SaveGroup';
					break;
				case 'exportTemplates':
					baseUrl += '/api/SiteConfiguration/' + (payload.action === 'create' ? 'AddExport' : 'UpdateExport');
					break;
			}
			return baseUrl;
		}
	}
};
